import React, { FC } from "react";
import { Snackbar, SnackbarCloseReason, useTheme } from "@mui/material";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { ITest } from "@websential/cosmic-v20";

/**
 * An alert displays a short, important message in a way that attracts the user's attention without interrupting the user's task.
 */

export interface IEnhancedSnackbarForLinkMessage extends ITest {
  /**
   * The snackbar is open or closed; default will be false
   */
  open: boolean;
  /**
   * The title of the alert.
   */
  title?: string;
  /**
   * The message displayed in the alert
   */
  message: any;
  /**
   * The severity of the alert like error or success.
   */
  severity: "error" | "info" | "success" | "warning";
  /**
   * The snackbar will auto hide after 5 sec, default true.
   */
  autoHide?: boolean;
  /**
   * The padded position of the snackbar; default padded with toolbar height
   */
  position?: "default" | "top-padded";
  /**
   * The callback function invoked when close button is pressed; if not passed then close button will not be shown
   */
  onCloseHandler?: (
    event: React.SyntheticEvent<any> | Event,
    reason: SnackbarCloseReason
  ) => void;
}

export const EnhancedSnackbarForLinkMessage: FC<IEnhancedSnackbarForLinkMessage> = ({
  title,
  message = "",
  severity = "success",
  autoHide = true,
  open = false,
  position = "top-padded",
  onCloseHandler,
}: IEnhancedSnackbarForLinkMessage) => {
  const theme = useTheme();
  return (
    <Snackbar
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={open}
      onClose={onCloseHandler}
      key={message}
      autoHideDuration={autoHide ? 5000 : null}
      sx={{
        top:
          position === "top-padded"
            ? `calc(${theme.mixins.toolbar.minHeight}px + ${theme.spacing(2)})`
            : "default",
        "@media (min-width: 600px)": {
          top:
            position === "top-padded"
              ? `calc(${theme.mixins.toolbar.minHeight}px + ${theme.spacing(2)})`
              : "default",
        },
      }}
    >
      <Alert severity={severity}>
        {title && title.length > 0 && <AlertTitle>{title}</AlertTitle>}
        {message}
      </Alert>
    </Snackbar>
  );
};
